import React, { useState, useEffect, useRef } from "react";
import { chatSessionsAPI } from "../utils/api";
import ConfirmationModal from "./ConfirmationModal";
import { FiFileText, FiMessageCircle, FiStar } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/TranslationContext";

export default function LeftSidebar({ chatHistory, selectedChatId, onSelectChat, refreshTrigger, onOpenDocumentManager, onCreateNewChat, onOpenPayment, collapsed, onDashboardClick }) {
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [tempSession, setTempSession] = useState(null);
  const { user } = useAuth();
  const { t } = useTranslation();

  // Smooth collapse/expand by deferring render switch until width transition completes
  const [renderCollapsed, setRenderCollapsed] = useState(collapsed);
  useEffect(() => {
    let timer;
    if (collapsed) {
      // Wait a bit so width transition finishes before swapping to compact DOM
      timer = setTimeout(() => setRenderCollapsed(true), 280);
    } else {
      // Swap to expanded DOM slightly before/at start of expand transition
      setRenderCollapsed(false);
    }
    return () => timer && clearTimeout(timer);
  }, [collapsed]);



  // Show temp session if selectedChatId is a temp id
  useEffect(() => {
    if (selectedChatId && selectedChatId.startsWith('temp_')) {
      setTempSession({
        id: selectedChatId,
        title: 'Starting new chat...',
      });
    } else {
      setTempSession(null);
    }
  }, [selectedChatId]);

  // Remove temp session when real sessions are loaded
  useEffect(() => {
    if (sessions.some(s => s.id && s.id.startsWith('temp_'))) {
      setSessions(sessions.filter(s => !s.id.startsWith('temp_')));
    }
  }, [sessions]);

  // Load chat sessions when component mounts
  useEffect(() => {
    setIsLoadingSessions(true);
    chatSessionsAPI.getSessions()
      .then(setSessions)
      .catch(() => setSessions([]))
      .finally(() => setIsLoadingSessions(false));
  }, [refreshTrigger]);

  // Clean UI: simple order by updated/created at, newest first
  const getDateFor = (s) => s.updatedAt || s.lastUpdated || s.createdAt || null;

  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;
    await chatSessionsAPI.deleteSession(sessionToDelete);
    setSessionToDelete(null);
    setShowDeleteModal(false);
    setIsLoadingSessions(true);
    chatSessionsAPI.getSessions()
      .then(setSessions)
      .catch(() => setSessions([]))
      .finally(() => setIsLoadingSessions(false));
    if (onCreateNewChat) onCreateNewChat();
  };



  // Collapsed sidebar UI
  if (renderCollapsed) {
    return (
      <div className="h-full w-16 bg-gray-50 dark:bg-gray-900 border-r border-gray-200/30 dark:border-gray-700/30 shadow-lg flex flex-col items-center py-4 relative transition-all duration-300" aria-expanded="false">
        {/* Dashboard icon for admin/super_admin */}
        {user && (user.role === 'admin' || user.role === 'super_admin') && (
          <button
            className="mb-6 p-2 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900 transition-colors duration-200"
            onClick={onDashboardClick}
            title="Dashboard"
          >
            <svg className="w-5 h-5 text-gray-900 dark:text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" aria-hidden>
              <rect x="3" y="3" width="7" height="7" rx="2" fill="currentColor" className="opacity-20" />
              <rect x="14" y="3" width="7" height="7" rx="2" fill="currentColor" className="opacity-20" />
              <rect x="14" y="14" width="7" height="7" rx="2" fill="currentColor" className="opacity-20" />
              <rect x="3" y="14" width="7" height="7" rx="2" fill="currentColor" className="opacity-20" />
              <rect x="3" y="3" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="2.2" />
              <rect x="14" y="3" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="2.2" />
              <rect x="14" y="14" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="2.2" />
              <rect x="3" y="14" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="2.2" />
            </svg>
          </button>
        )}
        <button
          className="mb-6 p-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-900 transition-colors duration-200"
          onClick={() => { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('expandSidebar')); }}
          title="Chat History"
        >
          <FiMessageCircle size={20} />
        </button>
      </div>
    );
  }

  // Expanded sidebar UI (existing)
  return (
    <div className="h-full w-full bg-gray-50 dark:bg-gray-900 border-r border-gray-200/30 dark:border-gray-700/30 shadow-lg flex flex-col relative overflow-hidden transition-all duration-300" aria-expanded="true">
      
      {/* Subtle decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 right-4 w-16 h-16 bg-indigo-50/50 dark:bg-indigo-900/20 rounded-full blur-2xl"></div>
        <div className="absolute bottom-40 left-4 w-12 h-12 bg-purple-50/50 dark:bg-purple-900/20 rounded-full blur-2xl"></div>
      </div>

      <div className="relative z-10 flex flex-col h-full">
        {/* Dashboard button for admin/super_admin */}
        {user && (user.role === 'admin' || user.role === 'super_admin') && (
          <div className="mb-4">
            <button
              className="w-full flex items-center gap-3 px-5 py-2.5 rounded-lg bg-white dark:bg-gray-800/90 shadow-md border-none transition-all duration-200 hover:scale-[1.03] hover:shadow-lg group relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-indigo-400"
              style={{ boxShadow: '0 1px 6px 0 rgba(30,41,59,0.10)' }}
              onClick={onDashboardClick}
            >
              <span className="flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-900 dark:text-indigo-400 group-hover:text-indigo-300 transition-colors duration-200" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                  <rect x="3" y="3" width="7" height="7" rx="2" fill="currentColor" className="opacity-20" />
                  <rect x="14" y="3" width="7" height="7" rx="2" fill="currentColor" className="opacity-20" />
                  <rect x="14" y="14" width="7" height="7" rx="2" fill="currentColor" className="opacity-20" />
                  <rect x="3" y="14" width="7" height="7" rx="2" fill="currentColor" className="opacity-20" />
                  <rect x="3" y="3" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="2.2" />
                  <rect x="14" y="3" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="2.2" />
                  <rect x="14" y="14" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="2.2" />
                  <rect x="3" y="14" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="2.2" />
                </svg>
              </span>
              {(() => { const label = t('nav.dashboard'); return (
                <span className="font-semibold text-gray-900 dark:text-white text-base tracking-tight ml-2">{label && label !== 'nav.dashboard' ? label : 'Dashboard'}</span>
              ); })()}
            </button>
          </div>
        )}

        {/* Quick Stats Section */}
        <div className="relative p-5 border-b border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-br from-gray-50/80 via-blue-50/30 to-indigo-50/20 dark:from-gray-800/80 dark:via-gray-800/50 dark:to-indigo-950/20">
          {/* Subtle background decorations */}
          <div className="absolute top-2 right-4 w-8 h-8 bg-indigo-100/30 dark:bg-indigo-900/20 rounded-full blur-lg"></div>
          <div className="absolute bottom-3 left-3 w-6 h-6 bg-purple-100/30 dark:bg-purple-900/20 rounded-full blur-lg"></div>
          
          <div className="relative mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Quick Stats</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">Your activity overview</p>
              </div>
            </div>
            
            <div className="space-y-3">
              {/* Chat Sessions Count */}
              <div className="flex items-center justify-between p-3 bg-white/60 dark:bg-gray-700/50 rounded-xl border border-gray-200/40 dark:border-gray-600/40">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <FiMessageCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">Chat Sessions</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Total conversations</div>
                  </div>
                </div>
                <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                  {sessions.length}
                </div>
              </div>

              {/* User Role Badge */}
              <div className="flex items-center justify-between p-3 bg-white/60 dark:bg-gray-700/50 rounded-xl border border-gray-200/40 dark:border-gray-600/40">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                    <FiStar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">Account Type</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Your current plan</div>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-md text-xs font-medium ${
                  user?.role === 'admin' || user?.role === 'super_admin' 
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    : user?.role === 'premium'
                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' 
                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                }`}>
                  {user?.role === 'admin' ? 'Admin' : 
                   user?.role === 'super_admin' ? 'Super Admin' : 
                   user?.role === 'premium' ? 'Premium' : 'Standard'}
                </div>
              </div>

              {/* Quick Tip */}
              <div className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/50 dark:to-purple-950/50 rounded-xl border border-indigo-200/40 dark:border-indigo-800/40">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg flex items-center justify-center mt-0.5">
                    <svg className="w-3 h-3 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-indigo-800 dark:text-indigo-300 mb-1">💡 Quick Tip</div>
                    <div className="text-xs text-indigo-700 dark:text-indigo-400 leading-relaxed">
                      Use the document selector in the chat area to quickly switch between your uploaded documents.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chat History Section */}
        <div className="flex-1 p-5 overflow-y-auto hide-scrollbar">
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-gradient-to-br from-green-500 to-blue-600 rounded-md flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{t('nav.chatHistory')}</h3>
            </div>
            <button
              onClick={onCreateNewChat}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-xs font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
              title="Start new chat"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              <span>New</span>
            </button>
          </div>
          
          {isLoadingSessions ? (
            <div className="text-center py-8 simple-fade-in">
              <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <div className="w-6 h-6 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8 simple-fade-in">
              <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('messages.noDocumentsAvailable')}</p>
            </div>
          ) : (
            <ul className="space-y-1 text-sm">
              {/* Show temp session if present */}
              {tempSession && (
                <li
                  key={tempSession.id}
                  className={`relative cursor-pointer px-4 py-3 rounded-xl transition-all duration-300 simple-hover-lift ${
                    selectedChatId === tempSession.id
                      ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25"
                      : "bg-white/70 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-600/50 hover:shadow-md"
                  }`}
                  style={{animationDelay: `0s`}}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${selectedChatId === tempSession.id ? 'bg-white' : 'bg-indigo-500'}`}></div>
                    <span className="truncate font-medium flex-1">{tempSession.title}</span>
                  </div>
                </li>
              )}
              {(sessions || [])
                .slice()
                .sort((a,b) => {
                  const da = getDateFor(a) ? new Date(getDateFor(a)).getTime() : 0;
                  const db = getDateFor(b) ? new Date(getDateFor(b)).getTime() : 0;
                  return db - da;
                })
                .map((session, index) => {
                  const isSelected = selectedChatId === session.id;
                  const dateText = getDateFor(session) ? new Date(getDateFor(session)).toLocaleDateString() : '';
                  return (
                    <li
                      key={session.id}
                      onClick={() => onSelectChat(session.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition ${
                        isSelected
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white/70 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      style={{animationDelay: `${index * 0.04}s`}}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-indigo-500'}`}></div>
                      <span className="truncate flex-1 text-sm">{session.title}</span>
                      {dateText && (
                        <span className={`text-[10px] ${isSelected ? 'text-indigo-100' : 'text-gray-400'}`}>{dateText}</span>
                      )}
                      <button
                        className={`p-1 rounded hover:bg-white/10 ${isSelected ? 'text-white/90' : 'text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30'}`}
                        title="Delete chat session"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSessionToDelete(session.id);
                          setShowDeleteModal(true);
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </li>
                  );
                })}
            </ul>
          )}
          {/* Create New Chat Button - Small and Subtle */}
          <div className="mt-3">
            <button
              onClick={() => {
                if (onCreateNewChat) {
                  onCreateNewChat();
                } else {
                  console.log('Creating new chat...');
                  // Fallback behavior if no handler is provided
                }
              }}
              className="w-full group relative overflow-hidden bg-gray-50/50 dark:bg-gray-800/30 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 border border-gray-100/80 dark:border-gray-700/50 hover:border-indigo-200 dark:hover:border-indigo-600/30 rounded-lg px-3 py-2 transition-all duration-200 hover:shadow-sm"
            >
              <div className="flex items-center justify-center gap-1.5 text-gray-600 dark:text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-200">
                <div className="w-4 h-4 rounded-md bg-gray-200/50 dark:bg-gray-600/50 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/40 flex items-center justify-center transition-all duration-200">
                  <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <span className="text-xs font-normal">{t('common.newChat')}</span>
              </div>
            </button>
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSessionToDelete(null); }}
        onConfirm={handleDeleteSession}
        title="Delete Chat Session?"
        message="Are you sure you want to delete this chat session? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
}